<template>
  <div class="w-full flex flex-col gap-6 text-left" data-testid="check-validation-panel">
    <!-- Q27 — fila aposentada: a rota continua respondendo (quem tem a URL salva
         precisa cair numa tela que explica, não num 404), mas nenhuma decisão
         sai daqui. Tabela, filtros e histórico seguem vivos. -->
    <div
      v-if="isRetired"
      class="w-full flex flex-col gap-3 rounded-lg border border-warning bg-warning/10 p-4 sm:flex-row sm:items-center sm:justify-between"
      role="status"
      data-testid="check-validation-retired-banner"
    >
      <div class="flex items-start gap-3">
        <svg class="w-5 h-5 shrink-0 text-warning" viewBox="0 0 24 24" aria-hidden="true">
          <path :d="mdiAlertOutline" fill="currentColor" />
        </svg>
        <div class="flex flex-col">
          <p class="text-sm font-bold text-textPrimary">
            Aposentada — somente leitura (histórico migrado para a fila de Registro de Plantão)
          </p>
          <p class="text-sm text-neutral50">
            Os registros lançados à mão no painel viraram Registro de Atividade. Aprovar, reprovar e
            reverter agora acontecem na fila de Registro de Plantão.
          </p>
        </div>
      </div>
      <OutlineButton
        text="Ir para Registro de Plantão"
        width="auto"
        height="36px"
        data-testid="check-validation-goto-ds-attendance"
        @click="goToDsAttendanceQueue"
      />
    </div>

    <CheckValidationFilterBar
      :search="search"
      :statuses="statuses"
      :period-days="periodDays"
      :health-unit-ids="healthUnitIds"
      :schedule-ids="scheduleIds"
      @search="checkValidationStore.setSearch"
      @filter-statuses="checkValidationStore.setStatuses"
      @filter-period="checkValidationStore.setPeriodDays"
      @filter-units="checkValidationStore.setHealthUnitIds"
      @filter-schedules="checkValidationStore.setScheduleIds"
      @clear-filters="checkValidationStore.clearFilters"
    />

    <div class="flex items-center justify-end">
      <div class="flex items-center gap-3">
        <!-- Q27 — o export NÃO é decisão: `GET /check-validation/export` segue
             200 com a fila aposentada, e o export em lote da fila unificada só
             chega na F4. O histórico precisa continuar exportável. -->
        <OutlineButton
          text="Exportar evidências"
          width="auto"
          height="36px"
          :prepend-icon="mdiTrayArrowDown"
          data-testid="check-validation-export-button"
          @click="isExportOpen = true"
        />
        <FilledButton
          text="Avaliar Pendentes"
          width="auto"
          height="36px"
          :prepend-icon="mdiClockOutline"
          :disabled="isRetired || !hasPendingRows"
          data-testid="check-validation-evaluate-pending-button"
          @click="evaluateFirstPending"
        />
      </div>
    </div>

    <CheckValidationTableSkeleton v-if="isLoading" :rows="limit" />

    <div
      v-else-if="hasError"
      class="w-full flex flex-col items-center justify-center gap-4 py-16 text-center"
      data-testid="check-validation-error-state"
    >
      <svg class="w-10 h-10 text-negative" viewBox="0 0 24 24" aria-hidden="true">
        <path :d="mdiAlertCircleOutline" fill="currentColor" />
      </svg>
      <p class="font-bold text-textPrimary">
        Não foi possível carregar os registros aguardando validação
      </p>
      <p
        v-if="loadError"
        class="text-sm text-neutral50"
        data-testid="check-validation-error-message"
      >
        {{ loadError }}
      </p>
      <OutlineButton
        text="Tentar novamente"
        width="200px"
        data-testid="check-validation-retry-button"
        @click="checkValidationStore.fetchQueue()"
      />
    </div>

    <CheckValidationEmptyState
      v-else-if="visibleRows.length === 0"
      :has-filters="hasActiveFilters"
      @clear-filters="checkValidationStore.clearFilters"
    />

    <CheckValidationTable
      v-else
      :rows="visibleRows"
      :is-evidence-unavailable="isRowEvidenceUnavailable"
      :is-retired="isRetired"
      @evaluate="onEvaluate"
      @details="onDetails"
    />

    <v-pagination
      v-if="!hasError && pageCount > 1"
      :model-value="page"
      :total-visible="7"
      :length="pageCount"
      :disabled="isLoading"
      density="comfortable"
      color="primary"
      data-testid="check-validation-pagination"
      @update:model-value="checkValidationStore.setPage"
    />

    <EvaluateCheckModal
      v-model="isEvaluateOpen"
      :row="evaluateRow"
      :saving="isSubmitting"
      :queue-position="pendingQueuePosition"
      :queue-total="pendingQueue.length"
      @approve="onApprove"
      @reject="onReject"
      @evidence-unavailable="checkValidationStore.markEvidenceUnavailable"
      @evidence-available="checkValidationStore.clearEvidenceUnavailable"
    />

    <CheckValidationDetailsModal
      v-model="isDetailsOpen"
      :row="detailsRow"
      :history="history"
      :is-history-loading="isHistoryLoading"
      :history-error="historyError"
      @reverse="onDetailsReverse"
    />

    <ReverseCheckModal
      v-model="isReverseOpen"
      :row="reverseRow"
      :saving="isSubmitting"
      @reverse="onConfirmReverse"
    />

    <ExportEvidencesModal
      v-model="isExportOpen"
      :exporting="isExporting"
      :filter-tags="exportFilterTags"
      @export="onExport"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import { mdiAlertCircleOutline, mdiAlertOutline, mdiClockOutline, mdiTrayArrowDown } from '@mdi/js'

import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { CheckValidationStatus } from '@/Network/Types/Responses/CheckValidationResponses'
import { useErrorToast } from '@/composables/useErrorToast'
import {
  CHECK_VALIDATION_RETIRED_MESSAGE,
  useCheckValidationStore
} from '@/pinia-store/useCheckValidationStore'
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import Network from '@/Network'
import { approvalsQueueLocation } from '@/modules/company/Approvals/Hub/queues'
import type { ExportCheckValidationParams } from '@/Network/Types/Requests/CheckValidationRequests'

import CheckValidationFilterBar from './components/CheckValidationFilterBar.vue'
import CheckValidationTable from './components/CheckValidationTable.vue'
import CheckValidationTableSkeleton from './components/CheckValidationTableSkeleton.vue'
import CheckValidationEmptyState from './components/CheckValidationEmptyState.vue'
import EvaluateCheckModal from './components/EvaluateCheckModal.vue'
import type {
  ApproveCheckPayload,
  RejectCheckModalPayload
} from './components/EvaluateCheckModal.vue'
import CheckValidationDetailsModal from './components/CheckValidationDetailsModal.vue'
import ReverseCheckModal from './components/ReverseCheckModal.vue'
import type { ReverseCheckPayload } from './components/ReverseCheckModal.vue'
import ExportEvidencesModal from './components/ExportEvidencesModal.vue'
import type { ExportPayload } from './components/ExportEvidencesModal.vue'
import { isCheckValidationStaleRowError } from './errors'
import { buildCheckValidationFilterTags } from './filterTags'
import type { CheckValidationRow } from './types'

const checkValidationStore = useCheckValidationStore()
const {
  visibleRows,
  page,
  limit,
  pageCount,
  search,
  statuses,
  periodDays,
  healthUnitIds,
  scheduleIds,
  isLoading,
  isSubmitting,
  hasError,
  loadError,
  hasActiveFilters,
  history,
  isHistoryLoading,
  historyError,
  isRetired
} = storeToRefs(checkValidationStore)

const router = useRouter()

// Q27 — quem aposenta esta fila é o backend (`manualCheckBlockEnabled`), pela
// MESMA verdade que bloqueia a inserção manual de check-in/out. Nenhuma flag de
// front: a tela só reflete o que a API já decidiu.
const dsAttendanceStore = useDsAttendanceStore()

const goToDsAttendanceQueue = () => {
  const location = approvalsQueueLocation('attendance')
  void router.push(location.query ? { path: location.path, query: location.query } : location.path)
}

const healthUnitStore = useHealthUnitStore()
const scheduleStore = useScheduleStore()
const { listHealthUnits } = storeToRefs(healthUnitStore)
const { listSchedules } = storeToRefs(scheduleStore)

const toast = useToast()
const { notifyError } = useErrorToast()

const exportFilterTags = computed(() =>
  buildCheckValidationFilterTags({
    search: search.value,
    statuses: statuses.value,
    onlyOverdue: false,
    periodDays: periodDays.value,
    unitIds: healthUnitIds.value.map(String),
    scheduleIds: scheduleIds.value.map(String),
    unitOptions: listHealthUnits.value.map((u) => ({ label: u.title, value: u.value })),
    scheduleOptions: listSchedules.value.map((s) => ({ label: s.title, value: s.value }))
  })
)

const isRowEvidenceUnavailable = (row: CheckValidationRow) =>
  checkValidationStore.isEvidenceUnavailable(row.checkId)

// ---- Avaliar ---------------------------------------------------------------
const isEvaluateOpen = ref(false)
const evaluateRow = ref<CheckValidationRow | null>(null)
const pendingQueue = ref<CheckValidationRow[]>([])
const pendingQueueIndex = ref(0)

const pendingQueuePosition = computed(() =>
  pendingQueue.value.length > 0 ? pendingQueueIndex.value + 1 : 0
)

const hasPendingRows = computed(() =>
  visibleRows.value.some((r) => r.status === CheckValidationStatus.PENDING_REVIEW)
)

const evaluateFirstPending = () => {
  const pending = visibleRows.value.filter((r) => r.status === CheckValidationStatus.PENDING_REVIEW)
  if (pending.length === 0) return
  pendingQueue.value = pending
  pendingQueueIndex.value = 0
  evaluateRow.value = pending[0]
  isEvaluateOpen.value = true
}

const onEvaluate = (row: CheckValidationRow) => {
  pendingQueue.value = []
  pendingQueueIndex.value = 0
  evaluateRow.value = row
  isEvaluateOpen.value = true
}

// ---- Detalhes --------------------------------------------------------------
const isDetailsOpen = ref(false)
const detailsRow = ref<CheckValidationRow | null>(null)

const onDetails = (row: CheckValidationRow) => {
  detailsRow.value = row
  isDetailsOpen.value = true
  void checkValidationStore.fetchHistory(row.checkId)
}

const onDetailsReverse = (row: CheckValidationRow) => {
  // Q27 — a reversão também é decisão: com a fila aposentada ela nem abre o
  // modal; a explicação vai num toast em vez de um clique que não faz nada.
  if (isRetired.value) {
    toast.info(CHECK_VALIDATION_RETIRED_MESSAGE)
    return
  }
  onReverse(row)
}

function advanceOrClose() {
  if (pendingQueue.value.length === 0) {
    isEvaluateOpen.value = false
    return
  }
  const nextIndex = pendingQueueIndex.value + 1
  if (nextIndex < pendingQueue.value.length) {
    pendingQueueIndex.value = nextIndex
    evaluateRow.value = pendingQueue.value[nextIndex]
  } else {
    pendingQueue.value = []
    isEvaluateOpen.value = false
  }
}

async function runDecision(
  action: () => Promise<void>,
  successMessage: string,
  closeModal: () => void
) {
  try {
    await action()
    toast.success(successMessage)
    closeModal()
    await checkValidationStore.fetchQueue()
  } catch (error) {
    if (isCheckValidationStaleRowError(error)) {
      notifyError(error, 'Este registro foi alterado por outro usuário. A lista foi atualizada.')
      closeEvaluateModal()
      await checkValidationStore.fetchQueue()
      return
    }
    notifyError(error, 'Não foi possível avaliar o registro')
  }
}

const closeEvaluateModal = () => {
  pendingQueue.value = []
  pendingQueueIndex.value = 0
  isEvaluateOpen.value = false
}

const onApprove = ({ checkId, version }: ApproveCheckPayload) =>
  runDecision(
    () => checkValidationStore.approve(checkId, version),
    'Registro aprovado',
    pendingQueue.value.length > 0 ? advanceOrClose : closeEvaluateModal
  )

const onReject = ({ checkId, version, reason, observation }: RejectCheckModalPayload) =>
  runDecision(
    () => checkValidationStore.reject(checkId, { version, reason, observation }),
    'Registro reprovado',
    pendingQueue.value.length > 0 ? advanceOrClose : closeEvaluateModal
  )

// ---- Reverter ---------------------------------------------------------------
const isReverseOpen = ref(false)
const reverseRow = ref<CheckValidationRow | null>(null)

const onReverse = (row: CheckValidationRow) => {
  reverseRow.value = row
  isReverseOpen.value = true
}

const closeReverseAndDetailsModals = () => {
  isReverseOpen.value = false
  isDetailsOpen.value = false
}

const onConfirmReverse = ({ checkId, version, justification }: ReverseCheckPayload) =>
  runDecision(
    () => checkValidationStore.reverse(checkId, version, justification),
    'Aprovação revertida. O registro voltou para análise.',
    closeReverseAndDetailsModals
  )

// ---- Exportar ---------------------------------------------------------------
const isExportOpen = ref(false)
const isExporting = ref(false)

async function onExport(payload: ExportPayload) {
  try {
    isExporting.value = true

    const params: ExportCheckValidationParams = { format: payload.format }

    if (payload.dateType === 'plantao') {
      params.startDate = payload.startDate
      params.endDate = payload.endDate
    } else {
      params.uploadStartDate = payload.startDate
      params.uploadEndDate = payload.endDate
    }

    if (statuses.value.length > 0) params.statuses = [...statuses.value]
    if (scheduleIds.value.length > 0) params.scheduleIds = [...scheduleIds.value]
    if (healthUnitIds.value.length > 0) params.healthUnitIds = [...healthUnitIds.value]

    await Network.checkValidation.exportEvidences(params)
    toast.success('Exportação concluída')
    isExportOpen.value = false
  } catch (error) {
    notifyError(error, 'Não foi possível exportar as evidências')
  } finally {
    isExporting.value = false
  }
}

onMounted(() => {
  void checkValidationStore.fetchQueue()
  // Tolerante a backend antigo: falha volta ao default (fila NÃO aposentada).
  void dsAttendanceStore.fetchRegistrationSettings()
})

onUnmounted(() => {
  checkValidationStore.$reset()
})
</script>
